import {useContext, useMemo} from 'react';

import config, {envIsLocal} from '../../config';
import {
  UserAccountType,
  useTeamOrganizationRolesQuery,
} from '../../generated/graphql';
import {MemberData} from '../../graphql/teamQuery';
import {useIsRolesDashboardEnabled} from '../../pages/OrgDashboard/OrgRoles/useIsRolesDashboardEnabled';
import {useViewer} from '../../state/viewer/hooks';
import {useAccountPrivileges} from '../../util/accounts/pricing';
import {useAdminModeActive} from '../../util/admin';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {RoleOption} from './TeamRoleOption';

export enum TeamMemberRole {
  ADMIN = 'admin',
  MEMBER = 'member',
  SERVICE = 'service',
  VIEW_ONLY = 'viewer',
}

export function useGetRoleOptions(entityName: string) {
  const viewer = useViewer();

  const isAdminModeActive = useAdminModeActive();
  const {selectedAccount} = useContext(AccountSelectorContext);
  const {loading: privilegesLoading, privileges} =
    useAccountPrivileges(selectedAccount);

  const {data, loading: isTeamOrgQueryLoading} = useTeamOrganizationRolesQuery({
    variables: {entityName},
  });

  const isEnterprise = selectedAccount?.isEnterprise;

  const hasViewOnlySeatsInSubscription =
    envIsLocal || privileges.numViewOnlySeats > 0;

  const viewerMember = data?.entity?.members?.find(
    m => m?.username === viewer?.username
  );

  const isViewerAdmin =
    isAdminModeActive || viewerMember?.role === TeamMemberRole.ADMIN;

  const viewerIsOrgAdmin = useMemo(() => {
    if (isTeamOrgQueryLoading || data?.entity?.organization?.members == null) {
      return false;
    }

    return (
      data?.entity?.organization?.members.find(
        mem => mem.username != null && mem.username === viewer?.username
      )?.role === 'admin'
    );
  }, [
    data?.entity?.organization?.members,
    viewer?.username,
    isTeamOrgQueryLoading,
  ]);

  const viewerIsServerAdmin = envIsLocal && viewer?.admin === true;

  const viewerTeamRole = viewerMember?.role;

  const canAnyMemberInviteTeamMembers =
    data?.entity?.settings?.membersCanInvite ?? false;

  const canViewerInviteTeamMembers =
    isViewerAdmin ||
    (canAnyMemberInviteTeamMembers && viewerTeamRole === TeamMemberRole.MEMBER);

  const canViewerManageRoles = viewerTeamRole !== TeamMemberRole.VIEW_ONLY;

  const roleManagementEnabled =
    !privilegesLoading && privileges.roleManagementEnabled;

  const isRolesDashboardEnabled = useIsRolesDashboardEnabled();

  const isLoading = isTeamOrgQueryLoading || privilegesLoading;

  const roleOptions: {isLoading: boolean; roleOptions: RoleOption[]} =
    useMemo(() => {
      const predefinedRoles: RoleOption[] = [
        {
          text: 'Admin',
          value: TeamMemberRole.ADMIN,
          isPremiumOption: false, // Admin is available on all plans, not a premium option
          viewerCanInviteOtherAsRole:
            isViewerAdmin || viewerIsOrgAdmin || viewerIsServerAdmin, // Only admins can add other users as admin
          viewerCanInviteSelfAsRole:
            isViewerAdmin || viewerIsOrgAdmin || viewerIsServerAdmin, // A user can only add themselves as an admin of a team if they're an org admin
        },
        {
          text: 'Member',
          value: TeamMemberRole.MEMBER,
          isPremiumOption: true,
          isPremiumFeatureDisabled: !roleManagementEnabled, // Member is only available on certain plans
          viewerCanInviteOtherAsRole: canViewerInviteTeamMembers, // Some teams don't allow members to invite other team members
          viewerCanInviteSelfAsRole: true, // Any org member can add themselves to any team they have permission to see (the email matching is validated on the backend)
        },
        {
          text: 'Service',
          value: TeamMemberRole.SERVICE,
          isPremiumOption: true,
          isPremiumFeatureDisabled:
            !roleManagementEnabled ||
            (!isEnterprise && !config.ENVIRONMENT_IS_PRIVATE), // Service accounts are only available on enterprise
          viewerCanInviteOtherAsRole: canViewerManageRoles, // Any team role can add a Service account on enterprise orgs
          viewerCanInviteSelfAsRole: false, // A real user can't be a service user
        },
        {
          text: 'View-Only',
          value: TeamMemberRole.VIEW_ONLY,
          isPremiumOption: true,
          isPremiumFeatureDisabled:
            !roleManagementEnabled || !hasViewOnlySeatsInSubscription, // View only is only available if the subscription allows it
          viewerCanInviteOtherAsRole: canViewerManageRoles, // Any team role can add a view only member
          viewerCanInviteSelfAsRole: canViewerManageRoles, // Any org role can invite themselves as a view only member
        },
      ];

      const customRoleOptions: RoleOption[] =
        data?.entity?.organization?.roles
          .filter(role => role.inheritedFrom)
          .map(role => {
            return {
              text: role.name,
              value: role.name,
              description: role.description,
              isPremiumOption: true,
              isPremiumFeatureDisabled:
                !roleManagementEnabled || !isRolesDashboardEnabled, // Custom roles are only available on enterprise plans
              viewerCanInviteSelfAsRole: canViewerManageRoles, // No restrictions on custom roles
              viewerCanInviteOtherAsRole: canViewerManageRoles, // No restrictions on custom roles
            };
          }) ?? [];

      return {
        isLoading,
        roleOptions: predefinedRoles.concat(customRoleOptions),
      };
    }, [
      canViewerInviteTeamMembers,
      hasViewOnlySeatsInSubscription,
      isEnterprise,
      isLoading,
      isRolesDashboardEnabled,
      isViewerAdmin,
      roleManagementEnabled,
      viewerIsOrgAdmin,
      data?.entity?.organization?.roles,
      canViewerManageRoles,
      viewerIsServerAdmin,
    ]);

  return roleOptions;
}

export function getRoleLabel(member: MemberData) {
  if (member.memberRole == null) {
    return '';
  }
  if (member.accountType === UserAccountType.Service) {
    return 'service';
  }
  if (member.pending) {
    return 'invited';
  }
  return member.memberRole.name;
}
